import {
  getNewOrderDefaultState,
  newOrderDefaultState,
  NewOrderLsKeys,
  NewOrderScenario,
  NewOrderType,
  TNewOrderForm,
  TNewOrderType,
} from "@/entities/new-order";
import {
  parseKeywordsToViews,
  parseViewsToKeywords,
} from "@/entities/new-order/utils";
import {
  getDataFromStorage,
  setDataToStorage,
} from "@/Utils/localStorageHelpers";
import { useDebouncedCallback } from "@mantine/hooks";
import { useLocalStorage } from "@uidotdev/usehooks";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";

const typeKeys = {
  [NewOrderType.rate]: "create-rate-keywords",
  [NewOrderType.install]: "create-install-keywords",
  [NewOrderType.install_bp]: "create-install-bp-keywords",
  [NewOrderType.install_with_views]: "create-install-with-views",
  [NewOrderType.review]: "create-review-keywords",
};

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const getFirstDateKeywordsValue = (country: {
  keywords: Record<string, unknown>;
}): unknown => {
  const dates = Object.keys(country.keywords);
  for (const date of dates) {
    if (date in country.keywords) {
      return country.keywords[date];
    }
  }
  return undefined;
};

const isViewsDateKeywords = (value: unknown): boolean => {
  if (!isObjectRecord(value)) {
    return false;
  }

  const keywordValues = Object.values(value);
  if (!keywordValues.length) {
    return false;
  }

  return keywordValues.some(
    (keywordValue) =>
      isObjectRecord(keywordValue) &&
      "installs" in keywordValue &&
      "views" in keywordValue,
  );
};

const isCountriesListCompatibleWithType = (
  countries: TNewOrderForm["countriesList"],
  orderType: TNewOrderType,
): boolean => {
  for (const country of countries) {
    if (!isObjectRecord(country.keywords)) {
      continue;
    }

    const firstDateValue = getFirstDateKeywordsValue({
      keywords: country.keywords as Record<string, unknown>,
    });

    if (firstDateValue === undefined) {
      continue;
    }

    const isReviewKeywords = Array.isArray(firstDateValue);
    const isViewsKeywords = isViewsDateKeywords(firstDateValue);

    if (orderType === NewOrderType.review) {
      if (!isReviewKeywords) {
        return false;
      }
      continue;
    }

    if (isReviewKeywords) {
      return false;
    }

    if (orderType === NewOrderType.install_with_views) {
      if (
        !isObjectRecord(firstDateValue) ||
        (isViewsKeywords === false && Object.keys(firstDateValue).length > 0)
      ) {
        return false;
      }
      continue;
    }

    if (isViewsKeywords) {
      return false;
    }
  }

  return true;
};

export function useNewOrderForm() {
  const [newOrderDraft, setNewOrderDraft] = useLocalStorage(
    NewOrderLsKeys.NEW_ORDER_DRAFT,
    newOrderDefaultState,
  );

  const methods = useForm<TNewOrderForm>({
    defaultValues: {
      ...newOrderDraft,
      activeScenario: NewOrderScenario.create,
    },
  });

  const { watch, getValues, setValue } = methods;

  const { countriesList, type, app } = watch();

  const prevOrderTypeRef = useRef<TNewOrderType | null>(null);
  const prevAppIdRef = useRef<number | null | undefined>(undefined);

  useEffect(() => {
    const currentAppId = app?.id ?? null;

    if (prevAppIdRef.current === undefined) {
      prevAppIdRef.current = currentAppId;
      return;
    }

    const isAppChanged = prevAppIdRef.current !== currentAppId;
    prevAppIdRef.current = currentAppId;

    if (
      !isAppChanged ||
      type !== NewOrderType.review ||
      !countriesList.length
    ) {
      return;
    }

    const clearedReviewCountries = countriesList.map((country) => {
      const clearedKeywords = Object.keys(country.keywords).reduce(
        (acc, date) => {
          acc[date] = [];
          return acc;
        },
        {} as Record<string, []>,
      );

      return {
        ...country,
        keywords: clearedKeywords,
      };
    });

    setValue("countriesList", clearedReviewCountries);
  }, [app?.id, countriesList, setValue, type]);

  useEffect(() => {
    if (!type) {
      Object.values(typeKeys).forEach((key) => localStorage.removeItem(key));
      return;
    }

    const oldType = prevOrderTypeRef.current;
    const newType = type;

    // Если тип не изменился, не делаем ничего
    if (oldType === newType) {
      return;
    }

    // Сохраняем/трансформируем данные при переходе с предыдущего типа
    if (oldType && countriesList.length > 0) {
      const isTransitionToViews =
        oldType === NewOrderType.install &&
        newType === NewOrderType.install_with_views;
      const isTransitionFromViews =
        oldType === NewOrderType.install_with_views &&
        newType === NewOrderType.install;
      const isViewsToOther =
        oldType === NewOrderType.install_with_views &&
        newType !== NewOrderType.install;

      if (isTransitionToViews) {
        // install → install_with_views: трансформируем и оставляем
        setValue("countriesList", parseKeywordsToViews(countriesList));
      } else if (isTransitionFromViews) {
        // install_with_views → install: трансформируем и оставляем
        setValue("countriesList", parseViewsToKeywords(countriesList));
      } else if (isViewsToOther) {
        // install_with_views → другой тип: конвертируем в install и сохраняем
        const convertedCountries = parseViewsToKeywords(countriesList);
        setDataToStorage(typeKeys[NewOrderType.install], convertedCountries);
        setValue("countriesList", []);
      } else {
        // Любой другой переход: сохраняем текущие данные и очищаем.
        // При переходе с review CountriesBlock (дочерний) срабатывает раньше
        // и успевает сбросить countriesList — берём данные напрямую из черновика.
        if (oldType === NewOrderType.review) {
          const draft = getDataFromStorage(
            NewOrderLsKeys.NEW_ORDER_DRAFT,
          ) as TNewOrderForm | null;
          const reviewCountries = draft?.countriesList;
          if (
            reviewCountries &&
            isCountriesListCompatibleWithType(reviewCountries, oldType)
          ) {
            setDataToStorage(typeKeys[oldType], reviewCountries);
          }
        } else if (isCountriesListCompatibleWithType(countriesList, oldType)) {
          setDataToStorage(typeKeys[oldType], countriesList);
        }
        setValue("countriesList", []);
      }
    }

    // Загружаем данные для нового типа только если есть oldType (не первая загрузка)
    if (oldType) {
      const savedCountries = getDataFromStorage(typeKeys[newType]);
      if (
        savedCountries &&
        isCountriesListCompatibleWithType(savedCountries, newType)
      ) {
        setValue("countriesList", savedCountries);
      }
    }

    prevOrderTypeRef.current = newType;
  }, [type]);

  const onSave = useDebouncedCallback((data: TNewOrderForm) => {
    setNewOrderDraft(data);
  }, 1000);

  const cancelSave = () => {
    onSave.cancel();
  };

  useEffect(() => {
    const subscription = watch(() => {
      const formData = getValues();
      onSave(formData);

      // Сохраняем также данные текущего типа в localStorage
      if (
        formData.type &&
        formData.countriesList.length > 0 &&
        isCountriesListCompatibleWithType(formData.countriesList, formData.type)
      ) {
        setDataToStorage(typeKeys[formData.type], formData.countriesList);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleReset = () => {
    // Отменяем отложенное сохранение
    cancelSave();

    // Очищаем все сохранённые данные
    localStorage.removeItem(NewOrderLsKeys.NEW_ORDER_DRAFT);
    Object.values(typeKeys).forEach((key) => localStorage.removeItem(key));

    // Создаем свежую копию дефолтного состояния
    const freshDefaultState = getNewOrderDefaultState();

    // Сбрасываем prevOrderTypeRef перед reset, чтобы избежать загрузки данных
    prevOrderTypeRef.current = freshDefaultState.type;

    // Сбрасываем форму
    methods.reset(
      { ...freshDefaultState, activeScenario: NewOrderScenario.create },
      { keepDefaultValues: false },
    );
  };

  return { methods, handleReset };
}
